/**
 * manager-global-button-feedback.js
 * Gerencia estados visuais de botões: loading, success, error.
 * Disponível como window.ButtonFeedback.
 *
 * Uso declarativo (ícones padrão):
 *   <button data-feedback>Comprar</button>
 *
 * Ícones customizados por botão (HTML/SVG como string):
 *   <button data-feedback
 *     data-feedback-loading="<svg>...</svg>"
 *     data-feedback-success="<svg>...</svg>"
 *     data-feedback-error="<svg>...</svg>">
 *     Comprar
 *   </button>
 *
 * Uso programático:
 *   window.ButtonFeedback.loading(btn)
 *   window.ButtonFeedback.success(btn)            → reseta após 2s
 *   window.ButtonFeedback.error(btn)              → reseta após 2.5s
 *   window.ButtonFeedback.reset(btn)
 */

class ButtonFeedback {
  constructor() {
    this._defaults = {
      loading: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="btn-feedback__spinner" aria-hidden="true"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>`,
      success: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`,
      error:   `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
    };
  }

  /** Salva o HTML original do botão antes da primeira substituição. */
  _save(btn) {
    if (btn.dataset.feedbackOriginal === undefined) {
      btn.dataset.feedbackOriginal = btn.innerHTML;
    }
  }

  /** Retorna o ícone customizado do botão ou o padrão do manager. */
  _icon(btn, type) {
    const key = `feedback${type.charAt(0).toUpperCase() + type.slice(1)}`;
    return btn.dataset[key] || this._defaults[type];
  }

  /**
   * Exibe estado de loading.
   * @param {HTMLElement} btn
   */
  loading(btn) {
    if (!btn) return;
    this._save(btn);
    btn.disabled = true;
    btn.dataset.feedbackState = 'loading';
    btn.innerHTML = this._icon(btn, 'loading');
  }

  /**
   * Exibe estado de sucesso e reseta após duration ms.
   * @param {HTMLElement} btn
   * @param {number} duration - ms antes de resetar (0 = não reseta)
   */
  success(btn, duration = 2000) {
    if (!btn) return;
    this._save(btn);
    btn.disabled = false;
    btn.dataset.feedbackState = 'success';
    btn.innerHTML = this._icon(btn, 'success');
    if (duration > 0) setTimeout(() => this.reset(btn), duration);
  }

  /**
   * Exibe estado de erro e reseta após duration ms.
   * @param {HTMLElement} btn
   * @param {number} duration - ms antes de resetar (0 = não reseta)
   */
  error(btn, duration = 2500) {
    if (!btn) return;
    this._save(btn);
    btn.disabled = false;
    btn.dataset.feedbackState = 'error';
    btn.innerHTML = this._icon(btn, 'error');
    if (duration > 0) setTimeout(() => this.reset(btn), duration);
  }

  /**
   * Restaura o botão ao estado original.
   * @param {HTMLElement} btn
   */
  reset(btn) {
    if (!btn || btn.dataset.feedbackOriginal === undefined) return;
    btn.disabled = false;
    btn.innerHTML = btn.dataset.feedbackOriginal;
    delete btn.dataset.feedbackOriginal;
    delete btn.dataset.feedbackState;
  }
}

window.ButtonFeedback = new ButtonFeedback();
